import { TextField } from '@mui/material';

/**
 * Reusable fiscal-year dropdown.
 *
 * Renders a native select populated with FY labels in the canonical
 * `YYYY-YY` format (e.g. "2025-26"). Defaults to a window of:
 *   - 3 prior FYs
 *   - current FY
 *   - 1 future FY
 * which covers every common use case in the app (TDS ledger, 24Q,
 * Form 16, declarations, returns) without becoming a sprawling list.
 *
 * Replaces the free-text "Fiscal year" TextField that previously let
 * users type "FY2025" or "2025/26" and silently break the API call.
 */
export type FYSelectProps = {
  value: string;
  onChange: (fy: string) => void;
  label?: string;
  size?: 'small' | 'medium';
  sx?: object;
  width?: number | string;
  /** Number of past FYs to include (default 3). */
  past?: number;
  /** Number of future FYs to include (default 1). */
  future?: number;
  /** Add an "All FYs" option at the top mapped to "". */
  allowEmpty?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
};

/** "YYYY-YY" label for the FY whose START year is `startYear` (Apr-Mar). */
export function fyLabel(startYear: number): string {
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}

/** Current Indian FY label for "today". April → next March. */
export function currentFYLabel(): string {
  const d = new Date();
  const startYear = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  return fyLabel(startYear);
}

export function buildFYOptions(past = 3, future = 1): string[] {
  const d = new Date();
  const start = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  const out: string[] = [];
  // Most recent first so the dropdown opens on relevant years.
  for (let i = future; i >= -past; i--) {
    out.push(fyLabel(start + i));
  }
  return out;
}

export function FYSelect({
  value, onChange,
  label = 'Fiscal year',
  size = 'small',
  sx,
  width = 140,
  past = 3,
  future = 1,
  allowEmpty = false,
  emptyLabel = 'All FYs',
  disabled }: FYSelectProps) {
  const opts = buildFYOptions(past, future);
  return (
    <TextField
      select size={size}
      label={label}
      sx={{ width, ...(sx || {}) }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ style: {} }}
      disabled={disabled}
    >
      {allowEmpty && <option value="">{emptyLabel}</option>}
      {opts.map((fy) => (
        <option key={fy} value={fy}>{fy}</option>
      ))}
    </TextField>
  );
}
